import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Star, Users, Clock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import Header from './Header';
import Footer from './Footer';
import { servicesAPI } from '../services/api';
import { useCompanyInfo } from '../hooks/useAPI';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: companyInfo } = useCompanyInfo();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const { data: serviceData, isLoading, error } = useQuery(
    ['service', slug],
    () => servicesAPI.getBySlug(slug).then(res => res.data),
    {
      enabled: !!slug
    }
  );

  // Default service data if API fails
  const defaultServices = [
    {
      id: 1,
      title: 'ERP Systems',
      slug: 'erp-systems',
      shortDescription: 'Custom enterprise resource planning systems designed to unify your business processes.',
      longDescription: 'Our Enterprise Resource Planning (ERP) systems are designed to streamline and integrate all your business processes into a single, unified platform. From inventory management to financial reporting, our custom ERP solutions provide real-time visibility and control over your entire operation.',
      features: [
        'Custom module development',
        'Real-time data synchronization',
        'Advanced reporting and analytics',
        'Multi-location support',
        'Role-based access control',
        'Mobile-responsive interface',
        'Third-party integrations',
        'Automated workflows'
      ],
      benefits: [
        'Increased operational efficiency',
        'Reduced manual errors',
        'Better decision-making with real-time data',
        'Improved customer service',
        'Cost reduction through automation',
        'Enhanced compliance and security'
      ],
      technologies: ['React', 'Node.js', 'PostgreSQL', 'Redis', 'Docker'],
      timeline: '3-6 months',
      teamSize: '4-6 developers',
      price: 'Starting from $50,000'
    },
    {
      id: 2,
      title: 'AI/ML Solutions',
      slug: 'ai-ml-solutions',
      shortDescription: 'Leverage intelligent automation and predictive analytics to gain a competitive edge.',
      longDescription: 'Transform your business with cutting-edge Artificial Intelligence and Machine Learning solutions. Our team develops custom AI models that can automate complex processes, predict trends, and provide intelligent insights to drive your business forward.',
      features: [
        'Custom machine learning models',
        'Natural language processing',
        'Computer vision applications',
        'Predictive analytics',
        'Automated decision systems',
        'Real-time model monitoring',
        'Data pipeline automation',
        'AI model deployment'
      ],
      benefits: [
        'Automated complex processes',
        'Predictive insights for better planning',
        'Enhanced customer experience',
        'Reduced operational costs',
        'Competitive advantage through innovation',
        'Scalable intelligent solutions'
      ],
      technologies: ['Python', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'AWS SageMaker'],
      timeline: '2-4 months',
      teamSize: '2-4 specialists',
      price: 'Starting from $30,000'
    },
    {
      id: 3,
      title: 'Cybersecurity',
      slug: 'cybersecurity',
      shortDescription: 'Comprehensive security audits and threat detection to safeguard your digital assets.',
      longDescription: 'Protect your business from evolving cyber threats with our comprehensive cybersecurity solutions. We provide end-to-end security assessments, implement robust defense systems, and ensure your organization stays protected against modern cyber threats.',
      features: [
        'Security vulnerability assessments',
        'Penetration testing',
        'Network security monitoring',
        'Incident response planning',
        'Compliance auditing',
        'Employee security training',
        'Security architecture design',
        '24/7 threat monitoring'
      ],
      benefits: [
        'Protection against cyber threats',
        'Regulatory compliance assurance',
        'Reduced risk of data breaches',
        'Enhanced customer trust',
        'Business continuity protection',
        'Cost savings from prevented attacks'
      ],
      technologies: ['Wireshark', 'Metasploit', 'Nessus', 'Splunk', 'Kali Linux'],
      timeline: '1-3 months',
      teamSize: '2-3 security experts',
      price: 'Starting from $25,000'
    }
  ];

  // Fallback to default if service not found
  const service = serviceData?.data || (error ? defaultServices.find(s => s.slug === slug) : null);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-lion-blue mb-4">Service Not Found</h1>
          <button
            onClick={() => navigate('/')}
            className="bg-lion-yellow text-lion-blue px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors duration-300"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <>
      <Helmet>
        <title>{service.title} - LionTek Services</title>
        <meta name="description" content={service.shortDescription} />
      </Helmet>

      <Header companyInfo={companyInfo} />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-lion-blue to-blue-800 text-white py-20">
          <div className="container mx-auto px-6">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.button
                variants={itemVariants}
                onClick={() => navigate('/')}
                className="flex items-center text-lion-yellow hover:text-yellow-300 mb-8 transition-colors duration-300"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </motion.button>
              
              <motion.h1 
                variants={itemVariants}
                className="text-4xl md:text-5xl font-poppins font-bold mb-6"
              >
                {service.title}
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="text-xl text-gray-200 max-w-3xl mb-8"
              >
                {service.fullDescription || service.longDescription || service.shortDescription}
              </motion.p>
              
              {(service.timeline || service.teamSize || service.price) && (
                <motion.div 
                  variants={itemVariants}
                  className="flex flex-wrap gap-6 text-sm"
                >
                  {service.timeline && (
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-lion-yellow" />
                      <span>Timeline: {service.timeline}</span>
                    </div>
                  )}
                  {service.teamSize && (
                    <div className="flex items-center">
                      <Users className="w-5 h-5 mr-2 text-lion-yellow" />
                      <span>Team: {service.teamSize}</span>
                    </div>
                  )}
                  {service.price && (
                    <div className="flex items-center">
                      <Star className="w-5 h-5 mr-2 text-lion-yellow" />
                      <span>{service.price}</span>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Left Column - Content */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-12"
              >
                {/* Features */}
                <motion.div variants={itemVariants}>
                  <h2 className="text-3xl font-poppins font-bold text-lion-blue mb-8">
                    Key Features
                  </h2>
                  <div className="space-y-3">
                    {service.features.map((feature, index) => (
                      <motion.div 
                        key={index} 
                        className="flex items-start group"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <div className="bg-lion-yellow/10 p-2 rounded-lg mr-4 flex-shrink-0 group-hover:bg-lion-yellow/20 transition-colors duration-300">
                          <CheckCircle className="w-5 h-5 text-lion-yellow" />
                        </div>
                        <span className="text-gray-700 leading-relaxed pt-1.5 text-base">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Benefits */}
                {service.benefits && service.benefits.length > 0 && (
                  <motion.div variants={itemVariants}>
                    <h2 className="text-3xl font-poppins font-bold text-lion-blue mb-8">
                      Business Benefits
                    </h2>
                    <div className="space-y-3">
                      {service.benefits.map((benefit, index) => (
                        <motion.div 
                          key={index} 
                          className="flex items-start group"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          viewport={{ once: true }}
                        >
                          <div className="bg-green-50 p-2 rounded-lg mr-4 flex-shrink-0 group-hover:bg-green-100 transition-colors duration-300">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          </div>
                          <span className="text-gray-700 leading-relaxed pt-1.5 text-base">{benefit}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Right Column - Image */}
              <motion.div
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="lg:sticky lg:top-24"
              >
                {service.image ? (
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                    <img
                      src={`${process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : ''}${service.image}`}
                      alt={service.title}
                      className="w-full h-auto object-cover transform group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback */}
                    <div className="hidden w-full aspect-[4/3] bg-gradient-to-br from-lion-blue to-blue-700 items-center justify-center">
                      <div className="text-center text-white p-8">
                        <div className="text-6xl mb-4">{service.icon || '💼'}</div>
                        <h3 className="text-2xl font-bold">{service.title}</h3>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full aspect-[4/3] rounded-2xl bg-gradient-to-br from-lion-blue to-blue-700 flex items-center justify-center shadow-2xl">
                    <div className="text-center text-white p-8">
                      <div className="text-6xl mb-4">{service.icon || '💼'}</div>
                      <h3 className="text-2xl font-bold">{service.title}</h3>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Technologies */}
            {service.technologies && service.technologies.length > 0 && (
              <motion.div
                variants={itemVariants}
                className="mt-16 bg-lion-gray p-8 rounded-lg"
              >
                <h2 className="text-2xl font-poppins font-bold text-lion-blue mb-6">
                  Technologies We Use
                </h2>
                <div className="flex flex-wrap gap-3">
                  {service.technologies.map((tech, index) => (
                    <span
                      key={index}
                      className="bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* CTA Section */}
            <motion.div
              variants={itemVariants}
              className="mt-16 text-center bg-gradient-to-r from-lion-blue to-blue-700 text-white p-8 rounded-lg"
            >
              <h2 className="text-3xl font-poppins font-bold mb-4">
                Ready to Get Started?
              </h2>
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                Let's discuss how our {service.title.toLowerCase()} can transform your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    navigate('/', { state: { scrollTo: 'contact' } });
                  }}
                  className="inline-flex items-center justify-center bg-lion-yellow text-lion-blue px-8 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors duration-300"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
                <a
                  href={`tel:${(companyInfo?.phone || '+15551234567').replace(/\s/g, '')}`}
                  className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-lion-blue transition-colors duration-300"
                >
                  Call Us Now
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer companyInfo={companyInfo} />
    </>
  );
};

export default ServiceDetailPage;